import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { API } from '../services/api';
import { subscribeToNotifications } from '../services/notificationsWS';

const NotificationContext = createContext();

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const { isAuthenticated, refreshUser } = useAuth();
    const [notifications, setNotifications] = useState([]);

    const [notificationHistory, setNotificationHistory] = useState(() => {
        const saved = localStorage.getItem('notification_history');
        return saved ? JSON.parse(saved) : [];
    });

    const seenInvitesRef = useRef(null);
    if (seenInvitesRef.current === null) {
        const saved = localStorage.getItem('seen_invites');
        seenInvitesRef.current = saved ? JSON.parse(saved) : [];
    }

    const removeNotification = useCallback((id) => {
        setNotifications(prev => prev.filter(notification => notification.id !== id));
    }, []);

    const addNotification = useCallback((typeOrConfig, messageArg, durationArg = 5000, isPersistentArg = false) => {
        const timestamp = new Date().toISOString();

        let type, message, duration, isPersistent, url, dbId;

        // Определяем формат аргументов: объект или позиционные
        if (typeof typeOrConfig === 'object' && typeOrConfig !== null && !Array.isArray(typeOrConfig)) {
            ({ type, message, duration = 5000, isPersistent = false, url = null, id: dbId = null } = typeOrConfig);
        } else {
            type = typeOrConfig;
            message = messageArg;
            duration = durationArg;
            isPersistent = isPersistentArg;
            url = null;
            dbId = null;
        }

        const id = dbId || (Date.now() + Math.random().toString(36).substr(2, 9));

        const newNotification = {
            id,
            type, // 'success', 'error', 'warning', 'info'
            message,
            duration,
            timestamp,
            isRead: false,
            url
        };

        setNotifications(prev => {
            const updated = [...prev, newNotification];
            return updated.slice(-3); // Сохраняем только последние 3
        });

        if (isPersistent) {
            setNotificationHistory(prev => {
                // Если уведомление с таким же id (или message при отсутствии id) уже есть, не дублируем
                if (prev.some(n => (dbId && n.id === dbId) || (!dbId && n.message === message))) {
                    return prev;
                }
                const updated = [newNotification, ...prev].slice(0, 50); // Keep last 50
                localStorage.setItem('notification_history', JSON.stringify(updated));
                return updated;
            });
        }

        if (duration > 0) {
            const timerId = setTimeout(() => {
                removeNotification(id);
            }, duration);
            return () => clearTimeout(timerId);
        }
    }, [removeNotification]);

    const markAsRead = useCallback(async (id) => {
        setNotificationHistory(prev => {
            const updated = prev.map(n => n.id === id ? { ...n, isRead: true } : n);
            localStorage.setItem('notification_history', JSON.stringify(updated));
            return updated;
        });

        // Если id числовой (пришел с бэкенда), отправляем запрос на бэкенд
        if (typeof id === 'number' || (typeof id === 'string' && !isNaN(Number(id)))) {
            try {
                await API.notifications.markAsRead(id);
            } catch (err) {
                console.error('Ошибка при пометке уведомления прочитанным на бэкенде:', err);
            }
        }
    }, []);

    const markAllAsRead = useCallback(async () => {
        setNotificationHistory(prev => {
            const updated = prev.map(n => ({ ...n, isRead: true }));
            localStorage.setItem('notification_history', JSON.stringify(updated));
            return updated;
        });

        try {
            await API.notifications.markAllAsRead();
        } catch (err) {
            console.error('Ошибка при пометке всех уведомлений прочитанными на бэкенде:', err);
        }
    }, []);

    const clearHistory = useCallback(() => {
        setNotificationHistory([]);
        localStorage.removeItem('notification_history');
    }, []);

    const removeFromHistory = useCallback((id) => {
        setNotificationHistory(prev => {
            const updated = prev.filter(n => n.id !== id);
            localStorage.setItem('notification_history', JSON.stringify(updated));
            return updated;
        });
    }, []);

    const fetchNotificationsHistory = useCallback(async () => {
        if (!isAuthenticated) return;
        try {
            const response = await API.notifications.getNotifications();
            const formatted = response.data.map(notif => ({
                id: notif.id,
                type: notif.priority === 'high' ? 'warning' : 'info',
                message: notif.message,
                duration: 0,
                timestamp: notif.created_at,
                isRead: notif.is_read,
                url: notif.payload?.url || notif.payload?.target_url
            }));
            setNotificationHistory(formatted);
            localStorage.setItem('notification_history', JSON.stringify(formatted));
        } catch (err) {
            console.error('Ошибка при загрузке истории уведомлений с бэкенда:', err);
        }
    }, [isAuthenticated]);

    // Загрузка истории при авторизации
    useEffect(() => {
        if (isAuthenticated) {
            fetchNotificationsHistory();
        } else {
            setNotificationHistory([]);
            localStorage.removeItem('notification_history');
        }
    }, [isAuthenticated, fetchNotificationsHistory]);

    // WebSocket subscription
    useEffect(() => {
        let socket = null;

        if (isAuthenticated) {
            const onNotification = (event) => {
                if (event.type === 'notification_created') {
                    const notif = event.data;
                    
                    // Вызываем визуальный Toast
                    addNotification({
                        id: notif.id,
                        type: notif.priority === 'high' ? 'warning' : 'info',
                        message: notif.message,
                        duration: 8000,
                        isPersistent: true,
                        url: notif.payload?.url || notif.payload?.target_url
                    });

                    // Если это одобрение роли, обновляем данные пользователя
                    if (notif.notif_type === 'system' && notif.message.toLowerCase().includes('одобрена') && (notif.message.toLowerCase().includes('роль') || notif.message.toLowerCase().includes('статус'))) {
                        refreshUser();
                    }
                }
            };

            socket = subscribeToNotifications(onNotification);
        }

        return () => {
            if (socket) {
                socket.close();
            }
        };
    }, [isAuthenticated, addNotification, refreshUser]);

    // Fallback Polling (раз в 5 минут на случай обрыва WS)
    useEffect(() => {
        let intervalId;

        const checkNotificationsFallback = async () => {
            if (!isAuthenticated || document.hidden) return;

            try {
                const response = await API.users.getNotifications();
                const invites = response.data.invitations || [];
                const apps = response.data.applications || [];

                let hasChanges = false;
                let currentSeen = [...seenInvitesRef.current];

                const processItems = (items) => {
                    items.forEach(item => {
                        const key = `${item.type}-${item.id}${(item.status ? `-${item.status}` : '')}`;
                        if (!currentSeen.includes(key)) {
                            currentSeen.push(key);
                            hasChanges = true;
                        }
                    });
                };

                processItems(invites);
                processItems(apps);

                if (hasChanges) {
                    seenInvitesRef.current = currentSeen;
                    localStorage.setItem('seen_invites', JSON.stringify(currentSeen));
                }
            } catch (err) {
                console.error('Fallback polling error:', err);
            }
        };

        if (isAuthenticated) {
            checkNotificationsFallback();
            intervalId = setInterval(checkNotificationsFallback, 300000); // 5 minutes
        }

        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [isAuthenticated]);

    return (
        <NotificationContext.Provider value={{
            notifications,
            notificationHistory,
            addNotification,
            removeNotification,
            markAsRead,
            markAllAsRead,
            clearHistory,
            removeFromHistory,
            fetchNotificationsHistory
        }}>
            {children}
        </NotificationContext.Provider>
    );
};
